import { Check, Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { generateHandoverPassword, passwordProblems } from '@jt/domain';

/**
 * Draws from the browser's cryptographic source, never `Math.random()`.
 *
 * `Math.random()` is seeded predictably and is not meant for anything anyone
 * would want to guess. The modulo below is very slightly biased for bounds that
 * do not divide 2^32; at these bounds the bias is far below anything that
 * matters for a password replaced on first use, and rejection sampling here
 * would be ceremony rather than security.
 */
const randomInt = (maxExclusive: number): number => {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0]! % maxExclusive;
};

/**
 * The temporary password an admin hands to somebody.
 *
 * Generated rather than typed. An admin inventing passwords for five people
 * produces five variations of one password, and the one they invent for a
 * technician is the one they can remember — which is the one that gets reused.
 * Generating it also means the admin never has to think about the rules.
 *
 * It is shown in plain text on purpose. This password's whole job is to be read
 * out loud or written down once; masking it would only mean the admin retypes
 * it into a message from memory and gets it wrong.
 */
export function HandoverPassword({
  value,
  onChange,
  context,
  label = 'Temporary password',
}: {
  value: string;
  onChange: (value: string) => void;
  /** The account it is for, so its own name and email are refused. */
  context?: { email?: string | null; name?: string | null };
  label?: string;
}) {
  const [copied, setCopied] = useState(false);

  const regenerate = () => {
    // Re-roll rather than accept a generated password the rules would refuse.
    // It happens when a random word collides with the person's own name, which
    // is rare enough to loop on and too confusing to show.
    let candidate = generateHandoverPassword(randomInt);
    for (let attempt = 0; attempt < 5; attempt += 1) {
      if (passwordProblems(candidate, context ?? {}).length === 0) break;
      candidate = generateHandoverPassword(randomInt);
    }
    onChange(candidate);
    setCopied(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused, and the password is on screen anyway.
      setCopied(false);
    }
  };

  const problems = value.length > 0 ? passwordProblems(value, context ?? {}) : [];

  return (
    <div>
      <span className="label">{label}</span>

      <div className="flex gap-2">
        <input
          type="text"
          required
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Press Generate"
          // Nothing here should be remembered by the browser: it belongs to
          // somebody else and is about to stop working.
          autoComplete="off"
          spellCheck={false}
          className={`input-field font-mono ${problems.length > 0 ? 'border-danger-400' : ''}`}
        />
        <button
          type="button"
          onClick={regenerate}
          className="btn-secondary shrink-0 px-3"
          title="Generate a new one"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Generate</span>
        </button>
        <button
          type="button"
          onClick={() => void copy()}
          disabled={!value}
          className="btn-secondary shrink-0 px-3 disabled:opacity-40"
          title="Copy"
        >
          {copied ? (
            <Check className="h-4 w-4 text-secondary-600" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="sr-only">{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>

      {problems.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {problems.map((problem) => (
            <li key={problem} className="text-xs text-danger-600">
              {problem}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          Send it to them now, then forget it. They are made to choose their own the first time
          they sign in, and this one stops working at that moment.
        </p>
      )}
    </div>
  );
}
